import { z } from 'zod';

import { emailSchema } from './auth-schemas.js';
import { RESERVED_ORG_SLUGS } from './constants.js';
import { userNameSchema } from './user-schemas.js';

const reserved = new Set<string>(RESERVED_ORG_SLUGS);

export const orgSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2, 'Slug must be at least 2 characters')
  .max(40, 'Slug must be 40 characters or fewer')
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Use lowercase letters, digits, and hyphens only')
  .refine((s) => !reserved.has(s), 'That slug is reserved');

export const hexColorSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-f]{6}$/i, 'Use a 6-digit hex color (e.g. #2b6cb0)')
  .transform((s) => s.toLowerCase());

export const createOrgInput = z.object({
  slug: orgSlugSchema,
  name: z.string().trim().min(1).max(120),
  initialAdminEmail: emailSchema,
  initialAdminName: userNameSchema,
});
export type CreateOrgInput = z.infer<typeof createOrgInput>;

export const updateBrandingInput = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    logoUrl: z.string().url().nullable().optional(),
    primaryColorHex: hexColorSchema.nullable().optional(),
  })
  .refine(
    (v) => v.name !== undefined || v.logoUrl !== undefined || v.primaryColorHex !== undefined,
    { message: 'Provide at least one field to update' },
  );
export type UpdateBrandingInput = z.infer<typeof updateBrandingInput>;

/** Response shape for branding fetched by an unauthenticated visitor on the
 *  org's login page. Only public-safe fields are included. */
export const orgPublicBranding = z.object({
  slug: orgSlugSchema,
  name: z.string(),
  logoUrl: z.string().url().nullable(),
  primaryColorHex: z.string().nullable(),
});
export type OrgPublicBranding = z.infer<typeof orgPublicBranding>;
